import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getDeviceList(
    $filter: deviceFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getDeviceList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        brand {
          id
          name
          deviceType
        }
        organization {
          id
          name
          shortName
        }
        kitDevice {
          kit {
            referenceId
          }
        }
        organizationId
        status
        type
        macAddress
        referenceId
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.DeviceListState = {
  result: null,
  error: null,
  loading: false
};

export const getDeviceListRequest = createAsyncThunk<
  TYPES.DeviceList,
  {
    filter?: TYPES.DeviceFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'device/get_device_list',
  async (
  params: {
    filter?: TYPES.DeviceFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getDeviceList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getDeviceListSlice = createSlice({
  name: 'get_device_list',
  initialState: initialState,
  reducers: {
    getDeviceListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getDeviceListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getDeviceListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getDeviceListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getDeviceListClear } = getDeviceListSlice.actions;

export const getDeviceList = createSelector(
  (state: RootState) => state.device.getDeviceList.result,
  (state: RootState) => state.device.getDeviceList.error,
  (state: RootState) => state.device.getDeviceList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getDeviceListSlice.reducer;